<template>
  <v-layout>
    <v-dialog :modelValue="dialog" @click:outside="$emit('updateDialog', false)" @keydown.esc="$emit('updateDialog', false)" max-width="750px" transition="dialog-bottom-transition">
      <v-card>
        <v-form @submit.prevent ref="form" v-model="valid" v-on:submit.prevent="onSubmit">
          <v-card-title>
            <span class="headline">Credenciales API{{ item.name ? " - " + item.name : "" }}</span>
          </v-card-title>

          <v-card-text>
            <div v-if="!has_credentials" class="text-center py-4">
              <p class="mb-4">Este cliente todavía no tiene credenciales API generadas.</p>
              <v-btn color="success" :loading="loading" @click="generate">Generar credenciales</v-btn>
            </div>

            <div v-else class="row">
              <div class="col-md-12">
                <a
                  class="d-inline-flex align-center px-4"
                  href="https://www.postman.com/wmsingeniar/workspace/wms-public/collection/39207050-fe097efd-5fef-4625-9f02-0b56c94a5612?action=share&source=copy-link&creator=39207050"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <v-icon size="small" class="mr-1">mdi-file-document-outline</v-icon>
                  Ver documentación de la API
                  <v-icon size="x-small" class="ml-1">mdi-open-in-new</v-icon>
                </a>

                <v-divider class="my-4"></v-divider>

                <v-text-field label="API ID" v-model="item.api_id" readonly class="locked-field" hint="Se genera automáticamente al regenerar las credenciales." persistent-hint>
                  <template v-slot:append>
                    <v-icon v-if="item.api_id" size="small" @click="copy(item.api_id)" title="Copiar">mdi-content-copy</v-icon>
                  </template>
                </v-text-field>

                <v-text-field
                  label="API Secret"
                  v-model="item.api_secret"
                  readonly
                  class="locked-field"
                  :type="show_secret ? 'text' : 'password'"
                  hint="Solo lectura. Para cambiarlo, tildar 'Regenerar credenciales'."
                  persistent-hint
                >
                  <template v-slot:append>
                    <v-icon size="small" class="mr-2" @click="show_secret = !show_secret" :title="show_secret ? 'Ocultar' : 'Mostrar'">{{ show_secret ? "mdi-eye" : "mdi-eye-off" }}</v-icon>
                    <v-icon v-if="item.api_secret" size="small" @click="copy(item.api_secret)" title="Copiar">mdi-content-copy</v-icon>
                  </template>
                </v-text-field>

                <div class="mt-6">
                  <v-btn color="warning" type="button" :loading="loading" @click="generate">Regenerar credenciales</v-btn>
                  <div class="text-caption mt-2">Genera un nuevo API ID y API Secret, sobrescribiendo los actuales.</div>
                </div>

                <v-divider class="my-4"></v-divider>

                <v-text-field label="Webhook URL" v-model="item.webhook_url" clearable :rules="item.webhook_url ? [rules.url] : []" hint="Opcional. Puede editarse o borrarse." persistent-hint></v-text-field>

                <v-tooltip v-if="saved_webhook_url" bottom :disabled="!has_unsaved_webhook_changes">
                  <template v-slot:activator="{ props }">
                    <div v-bind="props" style="margin-top: 10px">
                      <v-btn color="primary" variant="outlined" type="button" :loading="loading" :disabled="has_unsaved_webhook_changes" @click="testWebhook">Probar webhook</v-btn>
                    </div>
                  </template>
                  <span>Guardá los cambios en la Webhook URL antes de probarla.</span>
                </v-tooltip>
              </div>
            </div>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" text @click="$emit('updateDialog', false)">Cerrar</v-btn>
            <v-btn v-if="has_credentials" color="success" text type="submit" :loading="loading">Guardar</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import ValidMixin from "@/mixins/ValidMixin.vue";
export default {
  mixins: [ValidMixin],
  props: {
    service: Object,
    dialog: Boolean,
    editItem: Object,
  },
  data() {
    return {
      loading: false,
      show_secret: false,
      saved_webhook_url: "",
      item: {
        id: "",
        name: "",
        api_id: "",
        api_secret: "",
        webhook_url: "",
      },
    };
  },
  watch: {
    dialog() {
      if (this.dialog) {
        this.show_secret = false;

        if (this.$refs.form) {
          this.$refs.form.resetValidation();
        }

        if (this.editItem != null && this.editItem.id > 0) {
          this.item = {
            id: this.editItem.id,
            name: this.editItem.name,
            api_id: this.editItem.user ? this.editItem.user.api_id : "",
            api_secret: this.editItem.user ? this.editItem.user.api_secret : "",
            webhook_url: this.editItem.user ? this.editItem.user.webhook_url : "",
          };
          this.saved_webhook_url = this.item.webhook_url;
        }
      }
    },
  },
  computed: {
    has_credentials() {
      return !!this.item.api_id;
    },
    has_unsaved_webhook_changes() {
      return this.item.webhook_url !== this.saved_webhook_url;
    },
  },
  created() {
    this.rules.url = (value) => /^https?:\/\/.+/.test(value) || value == "" || value == null || "La URL debe comenzar con http:// o https://";
  },
  methods: {
    copy(value) {
      if (!value) return;
      navigator.clipboard.writeText(value);
      this.toast.success("Copiado al portapapeles.");
    },
    onSubmit() {
      if (this.valid) this.doSave(false);
    },
    generate() {
      this.doSave(true);
    },
    doSave(regenerate) {
      if (this.loading) return;
      this.loading = true;

      const data = {
        id: this.item.id,
        webhook_url: this.item.webhook_url,
        regenerate: regenerate,
      };

      this.service
        .saveApiCredentials(data)
        .then((response) => {
          if (response.status) {
            this.item.api_id = response.result.api_id;
            this.item.api_secret = response.result.api_secret;
            this.item.webhook_url = response.result.webhook_url;
            this.saved_webhook_url = response.result.webhook_url;
            this.toast.success("Credenciales guardadas correctamente.");
            this.$emit("loadList");
          } else {
            if (Object.prototype.hasOwnProperty.call(response, "message")) {
              this.toast.error(response.message);
            } else {
              this.toast.error(response.msg);
            }
          }
        })
        .catch((e) => {
          this.toast.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    testWebhook() {
      if (this.loading) return;
      this.loading = true;

      this.service
        .testWebhook({ id: this.item.id })
        .then((response) => {
          if (response.status) {
            this.toast.success(response.msg || "Webhook de prueba enviado correctamente.");
          } else {
            let error_msg = Object.prototype.hasOwnProperty.call(response, "message") ? response.message : response.msg;
            if (response.result && response.result.last_response_log) {
              error_msg += " - " + response.result.last_response_log;
              this.toast.error(error_msg, { autoClose: 15000 });
            } else {
              this.toast.error(error_msg);
            }
          }
        })
        .catch((e) => {
          this.toast.error(e);
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
/* Grisa el campo y bloquea foco/edición solo sobre el input,
   dejando interactivos los íconos del slot append (copiar/mostrar). */
.locked-field :deep(.v-field) {
  pointer-events: none;
  opacity: 0.7;
}
</style>
